import { Link } from 'react-router-dom'
import { STACKS } from '../data/stacks.js'
import Related from '../components/Related.jsx'
import FaqBlock from '../components/FaqBlock.jsx'
import { faqLd } from '../lib/faq.js'
import Seo from '../components/Seo.jsx'

const STACKS_FAQ = [
  { q: 'What is a peptide stack?', a: 'A stack is two or more peptides used toward a shared goal — for example BPC-157 with TB-500 for recovery, or CJC-1295 with Ipamorelin for growth-hormone support. They are usually paired because they act through different mechanisms that are discussed as complementary.' },
  { q: 'Should I use a pre-mixed blend or separate vials?', a: 'A blend sold as one vial fixes the ratio of each peptide and is convenient — one injection. Separate vials let you reconstitute and adjust each peptide independently, which gives more control. For a blend, use the blend calculator; for separate vials, reconstitute each with the peptide calculator.' },
  { q: 'How do I calculate the dose for a stack?', a: 'Reconstitute and dose each peptide by its own vial amount and target dose. For a fixed blend, the blend calculator splits a single draw across the components. Either way the reconstitution math is the same as for a single peptide — vial mg, water, and dose set the units to draw.' },
  { q: 'Are peptide stacks safe?', a: 'This is educational reference only, not medical advice or a recommendation to combine anything. Many peptides discussed here are research chemicals not approved for human use, and combining compounds can change how each behaves. Consult a qualified healthcare professional.' },
]

export default function Stacks() {
  return (
    <main className="page">
      <Seo
        title="Peptide Stacks — Combinations & Dosing | CalculateMyPeptides"
        description="A guide to common peptide stacks — BPC-157/TB-500 for recovery, CJC-1295/Ipamorelin for growth hormone, and more. What each combination is, why the peptides are paired, and how to calculate the dose."
        image="/og/stack.png"
        jsonLd={faqLd('Peptide stacks — FAQ', STACKS_FAQ)}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Stacks', path: '/stacks' }]}
      />
      <div className="container">
        <div style={{ marginBottom: 22 }}>
          <span className="pill blue" style={{ marginBottom: 8 }}>Reference</span>
          <h1 className="section-title">Peptide Stacks</h1>
          <p className="section-sub" style={{ maxWidth: '62ch' }}>
            A “stack” is two or more peptides used toward the same goal. These are the combinations you’ll see
            discussed most often — what each one is, why the peptides are paired, and links to reconstitute and dose
            them. Educational only, not medical advice.
          </p>
        </div>

        <div className="pep-grid">
          {STACKS.map((s) => (
            <Link key={s.slug} to={`/stacks/${s.slug}`} className="card pep-card fade-in">
              <div className="pep-card-top">
                <div className="wc-icon">{s.icon}</div>
                <h2 style={{ fontSize: '1.1rem', margin: '0 0 4px' }}>{s.name}</h2>
                <p className="pep-summary">{s.summary}</p>
              </div>
              <div className="pep-tags">
                {s.peptides.map((p) => <span key={p.slug} className="pill">{p.name}</span>)}
                <span className="pep-arrow">Open →</span>
              </div>
            </Link>
          ))}
        </div>

        <section className="page-narrow" style={{ margin: '32px auto 0' }}>
          <h2 className="pep-h2">Blends vs. separate vials</h2>
          <p>
            The same stack can be bought two ways. A <strong>blend</strong> is premixed in a single vial, so one
            injection delivers a fixed ratio of each peptide — convenient, but you can’t adjust one component
            without the others. <strong>Separate vials</strong> let you reconstitute and dose each peptide on its
            own, which gives more control at the cost of more injections.
          </p>
          <p>
            For a premixed blend, our <Link to="/blend">blend calculator</Link> splits a single draw across the
            components; for separate vials, reconstitute each one with the <Link to="/calculator">peptide
            calculator</Link>. Either way the underlying math is identical to a single peptide — vial amount,
            bacteriostatic water, and dose set the units to draw.
          </p>
          <FaqBlock title="Peptide stack FAQ" items={STACKS_FAQ} />
        </section>

        <Related
          title="Keep exploring"
          links={[
            { to: '/blend', label: 'Blend calculator', desc: 'For a stack premixed in one vial' },
            { to: '/calculator', label: 'Peptide calculator', desc: 'Reconstitute each peptide separately' },
            { to: '/peptides', label: 'Peptide library', desc: 'Profiles for every compound' },
            { to: '/glossary', label: 'Glossary', desc: 'Look up any term' },
          ]}
        />

        <p className="muted small" style={{ marginTop: 24, maxWidth: '70ch' }}>
          ⚠️ Educational reference only — not medical advice, and not a recommendation to combine or use any
          compound. Many peptides referenced are research chemicals not approved for human use. Consult a qualified
          healthcare professional.
        </p>
      </div>
    </main>
  )
}
